// Pricing Page - VinciKit API Subscription Plans (Dark Theme)
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Zap, Rocket, Building2, Sparkles, ArrowLeft } from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = 'https://us-central1-vincikit-bd9e1.cloudfunctions.net';

// Plan icons
const PLAN_ICONS = {
  free: Sparkles,
  starter: Zap,
  pro: Rocket,
  enterprise: Building2
};

// Plan features
const PLAN_FEATURES = {
  free: [
    '10 API calls per month',
    'Basic stack recommendations',
    'Community support',
    'Perfect for testing'
  ],
  starter: [
    '100 API calls per month',
    '$0.15 per additional call',
    'Full stack recommendations',
    'Email support',
    'Usage analytics'
  ],
  pro: [
    '500 API calls per month',
    '$0.08 per additional call',
    'Priority recommendations',
    'Priority support',
    'Advanced analytics',
    'Custom integrations'
  ],
  enterprise: [
    '5,000 API calls per month',
    '$0.03 per additional call',
    'Dedicated support',
    'Custom AI models',
    'SLA guarantee',
    'Volume discounts available'
  ]
};

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_BASE}/getPricing`);
      const data = await response.json();
      setPlans(data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId, planId) => {
    if (planId === 'free') {
      // For free plan, just redirect to signup/dashboard
      navigate('/login');
      return;
    }

    setCheckoutLoading(planId);
    try {
      const response = await fetch(`${API_BASE}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          customerEmail: user?.email,
          successUrl: `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing`
        })
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1117] relative overflow-hidden">
      {/* Gradient background overlay */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative px-6 py-4 sticky top-0 bg-[#0d1117]/80 backdrop-blur-md z-50 border-b border-white/10">
        <nav className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-xl flex items-center justify-center">
              <GoldenSpiral className="w-6 h-6" color="white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-white">VinciKit</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Hero Section */}
      <div className="relative text-center py-16 px-4">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight"
        >
          VinciKit API Pricing
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-xl text-gray-400 max-w-2xl mx-auto"
        >
          Get AI-powered technology stack recommendations for your projects.
          Choose a plan that fits your needs.
        </motion.p>
      </div>

      {/* Pricing Cards */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => {
              const Icon = PLAN_ICONS[plan.id] || Sparkles;
              const features = PLAN_FEATURES[plan.id] || [];
              const isPopular = plan.id === 'pro';

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative rounded-2xl p-6 backdrop-blur-sm ${
                    isPopular
                      ? 'bg-gradient-to-b from-pink-500/20 via-purple-500/20 to-cyan-500/20 border-2 border-purple-500'
                      : 'bg-white/5 border border-white/10'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white text-sm font-medium px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <div className={`inline-flex p-3 rounded-xl mb-4 ${
                      isPopular ? 'bg-purple-500/20' : 'bg-white/5'
                    }`}>
                      <Icon className={`w-6 h-6 ${isPopular ? 'text-purple-400' : 'text-gray-400'}`} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-white">
                        ${plan.price}
                      </span>
                      <span className="text-gray-400">/month</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">
                      {plan.callsIncluded} API calls included
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isPopular ? 'text-purple-400' : 'text-green-400'
                        }`} />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(plan.priceId, plan.id)}
                    disabled={checkoutLoading === plan.id}
                    className={`w-full py-3 px-4 rounded-xl font-medium transition-all ${
                      isPopular
                        ? 'bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 text-white hover:opacity-90'
                        : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {checkoutLoading === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Processing...
                      </span>
                    ) : plan.id === 'free' ? (
                      'Get Started Free'
                    ) : (
                      'Subscribe Now'
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* How it works */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-white mb-4 tracking-tight">
            How it works
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mt-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mx-auto mb-4 border border-pink-500/30">
                <span className="text-2xl font-bold text-pink-400">1</span>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Subscribe</h3>
              <p className="text-gray-400 text-sm">Choose a plan and complete checkout to get your API key.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4 border border-purple-500/30">
                <span className="text-2xl font-bold text-purple-400">2</span>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Integrate</h3>
              <p className="text-gray-400 text-sm">Use your API key to call VinciKit from your application.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mx-auto mb-4 border border-cyan-500/30">
                <span className="text-2xl font-bold text-cyan-400">3</span>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Build</h3>
              <p className="text-gray-400 text-sm">Get AI-powered recommendations for your tech stack.</p>
            </div>
          </div>
        </div>

        {/* API Example */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-4 text-center tracking-tight">
            Simple API Integration
          </h2>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <pre className="text-sm text-gray-300 overflow-x-auto">
              <code>{`curl -X POST https://us-central1-vincikit-bd9e1.cloudfunctions.net/recommend \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: vk_your_api_key_here" \\
  -d '{
    "goal": "Build a smart home automation system",
    "projectScope": "hobby"
  }'`}</code>
            </pre>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative px-6 py-10 border-t border-white/10 bg-[#0d1117]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
              <GoldenSpiral className="w-5 h-5" color="white" />
            </div>
            <span className="font-semibold text-white">VinciKit</span>
          </div>
          <div className="flex items-center gap-8">
            <Link to="/privacy" className="text-sm text-gray-500 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-sm text-gray-500 hover:text-white transition-colors">Terms</Link>
          </div>
          <p className="text-sm text-gray-500">
            © 2025 VinciKit. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
